import { Component, EventEmitter, Input, Output, type OnInit, ViewChild, inject } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { SharedService } from '@shared/services/shared.service';
import { IIndividualApplicant } from 'app/models/applicants/individual-applicant';
import { IStatus } from 'app/models/licenses/status';
import { IndividualApplicantService } from 'app/services/applicant/individual-applicant.service';
import { AddressComponent } from '../address/address.component';
import { DocumentComponent } from '../document/document.component';

@Component({
  selector: 'individual-applicant',
  templateUrl: './individual-applicant.component.html',
  styleUrls: ['./individual-applicant.component.css']
})
export class IndividualApplicantComponent implements OnInit {
  @ViewChild(AddressComponent) addresses: AddressComponent;
  @ViewChild(DocumentComponent) documents: DocumentComponent;

  @Input() entityId: number = 0;
  @Input() entityType: string = '';
  @Input() statuses: IStatus[] = [];
  @Output() applicantSelected: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() closeLicense: EventEmitter<boolean> = new EventEmitter<boolean>();

  public addingApplicant: boolean = false;
  public newApplicantCreated: boolean = false;
  public includeArchived: boolean = false;
  public applicant: IIndividualApplicant = {
    id: 0,
    applicantId: '',
    ssn: '',
    ssnEncrypted: false,
    noSSN: false,
    oregonResident: false,
    legalFirstName: '',
    legalMiddleName: '',
    legalLastName: '',
    preferredFirstName: '',
    preferredMiddleName: '',
    preferredLastName: '',
    alternateNames: '',
    dob: '',
    phone: '',
    alternatePhone: '',
    email: '',
    language: '',
    archived: false
  };
  public applicants: IIndividualApplicant[] = [];
  public applicantForm = new FormGroup({
    ssn: new FormControl(''),
    noSsn: new FormControl(false),
    legalFirstName: new FormControl('', Validators.required),
    legalMiddleName: new FormControl(''),
    legalLastName: new FormControl('', Validators.required),
    preferredFirstName: new FormControl(''),
    preferredMiddleName: new FormControl(''),
    preferredLastName: new FormControl(''),
    alternateNames: new FormControl(''),
    dob: new FormControl<Date>(null, Validators.required),
    phone: new FormControl('', [Validators.required, Validators.pattern(/^\(?([0-9]{3})\)?[-]?([0-9]{3})[-]?([0-9]{4}).*$/)]),
    alternatePhone: new FormControl('', [Validators.pattern(/^\(?([0-9]{3})\)?[-]?([0-9]{3})[-]?([0-9]{4}).*$/)]),
    email: new FormControl('', [Validators.required, Validators.email]),
    confirmEmail: new FormControl('', [Validators.required, Validators.email]),
    language: new FormControl(''),
    oregonResident: new FormControl(false)
  }, {validators: [
    this.ssnValidator(),
    this.emailValidator()
  ]});

  public applicantColumns: string [] = ['legalFirstName', 'legalLastName', 'email', 'archived', 'actions'];
  public applicantSource = new MatTableDataSource<IIndividualApplicant>(this.applicants);
  @ViewChild('applicantPaginator', {static: false}) applicantPaginator: MatPaginator;
  @ViewChild('applicantSort', {static: false}) applicantSort: MatSort;

  public sharedService = inject(SharedService);
  public applicantService = inject(IndividualApplicantService);

  ngOnInit(): void {
    this.getApplicants(this.includeArchived);
  }

  getApplicants(includeArchived: boolean): void {
    this.includeArchived = includeArchived;
    this.applicantService.getApplicants(this.entityId, this.entityType, includeArchived).subscribe({
      next: response => this.applicants = response,
      error: e => {
        this.sharedService.toastr.error("Could not get individual applicants");
        console.log('error', e);
      },
      complete: () => {
        if(this.entityType === "IndividualLicense"){
          this.setActiveApplicant();
        }
        else{
          this.updateApplicantTable();
        }
      }
    });
  }

  setActiveApplicant(): void {
    if(this.applicants.length === 1){
      this.applicant = this.applicants[0];
    }
    else{
      this.applicant = this.applicants.find(a => !a.archived);
    }

    this.updateApplicantForm(this.applicant);
    this.addingApplicant = true;
  }

  updateApplicantTable(): void {
    setTimeout(() => {
      this.applicantSource.data = this.applicants;
      this.applicantSource.sort = this.applicantSort;
      this.applicantSource.paginator = this.applicantPaginator;
    }, 500);
  }

  updateApplicantForm(applicant: IIndividualApplicant): void {
    this.applicantForm.reset({
      ssn: applicant.ssn,
      noSsn: applicant.noSSN,
      legalFirstName: applicant.legalFirstName,
      legalMiddleName: applicant.legalMiddleName,
      legalLastName: applicant.legalLastName,
      preferredFirstName: applicant.preferredFirstName,
      preferredMiddleName: applicant.preferredMiddleName,
      preferredLastName: applicant.preferredLastName,
      alternateNames: applicant.alternateNames,
      phone: applicant.phone,
      alternatePhone: applicant.alternatePhone,
      email: applicant.email,
      confirmEmail: applicant.email,
      language: applicant.language,
      oregonResident: applicant.oregonResident
    });

    if(applicant.dob !== '' && applicant.dob !== null){
      this.applicantForm.get('dob').setValue(this.sharedService.getDateFromString(applicant.dob));
    }
    else{
      this.applicantForm.get('dob').setValue(null);
    }
  }

  updateApplicant(): void {
    let form = this.applicantForm.value;
    this.applicant.ssn = form.ssn;
    this.applicant.noSSN = form.noSsn;
    this.applicant.oregonResident = form.oregonResident;
    this.applicant.legalFirstName = form.legalFirstName;
    this.applicant.legalMiddleName = form.legalMiddleName;
    this.applicant.legalLastName = form.legalLastName;
    this.applicant.preferredFirstName = form.preferredFirstName;
    this.applicant.preferredMiddleName = form.preferredMiddleName;
    this.applicant.preferredLastName = form.preferredLastName;
    this.applicant.alternateNames = form.alternateNames;
    this.applicant.dob = form.dob?.toDateString();
    this.applicant.phone = form.phone;
    this.applicant.alternatePhone = form.alternatePhone;
    this.applicant.email = form.email;
    this.applicant.language = form.language;
  }

  getEmptyApplicant(): IIndividualApplicant {
    return {
      id: 0,
      applicantId: '',
      ssn: '',
      ssnEncrypted: false,
      noSSN: false,
      oregonResident: false,
      legalFirstName: '',
      legalMiddleName: '',
      legalLastName: '',
      preferredFirstName: '',
      preferredMiddleName: '',
      preferredLastName: '',
      alternateNames: '',
      dob: '',
      phone: '',
      alternatePhone: '',
      email: '',
      language: '',
      archived: false
    }
  }

  selectApplicant(applicant: IIndividualApplicant): void {
    this.applicant = applicant;
    this.addingApplicant = true;
    this.updateApplicantForm(applicant);
    this.applicantSelected.emit(true);
  }

  toggleApplicantForm(adding: boolean): void {
    this.addingApplicant = adding;
    this.updateApplicantForm(this.getEmptyApplicant());
    if(!adding){
      if(this.newApplicantCreated){
        this.deleteApplicant();
      }
      else{
        this.updateApplicantTable();
        this.applicant = this.getEmptyApplicant();
      }
    }
    this.applicantSelected.emit(adding);
  }

  createApplicant(): void {
    this.applicantService.createApplicant(this.applicant, this.entityId, this.entityType).subscribe({
      next: response => {
        this.applicants.push(response);
        this.applicant = response;
      },
      error: e => {
        this.sharedService.toastr.error("Could Not Create Applicant");
        console.log("create applicant error", e);
      },
      complete: () => {
        this.toggleApplicantForm(true);
        this.newApplicantCreated = true;
      }
    });
  }

  saveApplicant(): void {
    this.updateApplicant();
    this.applicantService.updateApplicant(this.applicant).subscribe({
      error: e => {
        this.sharedService.toastr.error("Could Not Update Applicant");
        console.log("update applicant error", e);
      },
      complete: () => {
        this.newApplicantCreated = false;
        this.sharedService.toastr.success("Applicant Updated");
      }
    });
  }

  archiveApplicant(applicant: IIndividualApplicant, archive: boolean): void {
    let archiving: string = archive? 'Archive ' : 'Un-Archive ';
    this.sharedService.openConfirm(archiving + 'this entity applicant?');
    this.sharedService.confirmed().subscribe(
      confirmed => {
        if(confirmed){
          this.applicantService.archiveApplicant(applicant.id, archive).subscribe({
            complete: () => {
              applicant.archived = archive;
              if(archive){
                this.applicants = this.applicants.filter(a => a.id !== applicant.id);
              }
              this.toggleApplicantForm(false);
            },
            error: e => {
              this.sharedService.toastr.error("Could Not Archive Applicant");
              console.log('error archiving entity applicant', e);
            }
          });
        }
      }
    );
  }

  close(save: boolean): void {
    if(save){
      this.saveApplicant();
    }
    this.closeLicense.emit(save);
  }

  deleteApplicant(): void {
    this.applicantService.deleteApplicant(this.applicant.id).subscribe({
      complete: () => {
        this.applicants = this.applicants.filter(a => a.id !== this.applicant.id);
        this.newApplicantCreated = false;
        this.updateApplicantTable();
        this.applicant = this.getEmptyApplicant();
      },
      error: e => console.log('error deleting applicant', e)
    });
  }

  public get locked(): boolean {
    return this.statuses.some(s => s.label === this.sharedService.submitted);
  }

  //Custom Validators
  ssnValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const ssn = control.value.ssn;
      const noSsn = control.value.noSsn;
      if(noSsn){
        return null;
      }
      return (ssn !== null && ssn !== '' && ssn !== undefined) ? null : { ssnRequired: true };
    }
  }

  validateSsn(): boolean {
    if (this.applicantForm.hasError('ssnRequired') && this.applicantForm.get('ssn').touched) {
      this.applicantForm.get('ssn').setErrors(['ssnRequired']);
      return true;
    }
    this.applicantForm.get('ssn').clearValidators();
    this.applicantForm.get('ssn').updateValueAndValidity();
    return false;
  }

  emailValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const email = control.value.email;
      const confirmation = control.value.confirmEmail;
      return email === confirmation ? null : { emailConfirmed: true };
    }
  }

  validateEmail(): boolean {
    if (this.applicantForm.hasError('emailConfirmed') && this.applicantForm.get('confirmEmail').touched) {
      this.applicantForm.get('confirmEmail').setErrors(['emailConfirmed']);
      return true;
    }
    this.applicantForm.get('confirmEmail').clearValidators();
    this.applicantForm.get('confirmEmail').updateValueAndValidity();
    return false;
  }
}
